import { type CSSObject } from "@emotion/react";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useHistory } from "react-router";
import { Breakpoints, Colors, Experience, Icon, IconBlock } from "swing-components";

import { AlertError, ButtonScoreWrapper, ContentSingleColumn } from "~components";
import { createWorkExperienceEditUrl, createWorkExperienceNewUrl } from "~pages";
import { GET, ProfileData, translate } from "~utils";

import { ScorePage } from "../../ScoreTemplates";

export function WorkExperienceLanding() {
  const { data, error, isLoading } = useQuery<{ data: ProfileData }, AxiosError>({
    queryKey: ["fetchProfile"],
    queryFn: () => GET("/api/sub/profile"),
  });

  return (
    <WorkExperienceLandingView
      workExperience={data?.data.experience}
      error={error}
      isLoading={isLoading}
    />
  );
}

type WorkExperienceLandingViewProps = {
  workExperience?: Experience[];
  error?: AxiosError | null;
  isLoading?: boolean;
};

function WorkExperienceLandingView(props: WorkExperienceLandingViewProps) {
  const { workExperience = [], error, isLoading } = props;

  /* Hooks */
  const history = useHistory();

  return (
    <ScorePage
      title={translate("PAGE_TITLE_WORK_EXPERIENCE_LANDING")}
      isLoading={isLoading}
      hasBack
    >
      {error ? (
        <AlertError margin="0px" />
      ) : (
        <ContentSingleColumn>
          <div css={styles.workExperienceLandingContainer}>
            <span>{translate("PAGE_TITLE_WORK_EXPERIENCE_LANDING_SUB_HEADER")}</span>
            {workExperience.length > 0 && (
              <div css={styles.workExperienceContent}>
                {workExperience.map((exp, idx) => {
                  const { id, organization } = exp;
                  const isLastExperience = idx === workExperience.length - 1;
                  return (
                    <div key={id}>
                      <IconBlock
                        content={<span>{organization}</span>}
                        rightIcon={{
                          icon: <Icon name="Edit" color={Colors.blue500} />,
                          onClick: () => history.push(createWorkExperienceEditUrl(`${id}`)),
                        }}
                        hasDivider={!isLastExperience}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {(!workExperience || workExperience.length < 5) && (
              <div style={{ backgroundColor: "var(--swing-white300)" }}>
                <ButtonScoreWrapper
                  buttonPrimary={{
                    label: `+ ${translate("PAGE_TITLE_WORK_EXPERIENCE_EDIT")}`,
                    expand: "block",
                    onClick: () => history.push(createWorkExperienceNewUrl()),
                  }}
                />
              </div>
            )}
          </div>
        </ContentSingleColumn>
      )}
    </ScorePage>
  );
}

/********** Styles **********/
const styles = {
  workExperienceLandingContainer: {
    padding: "16px 16px 0",

    [Breakpoints.mediumDeviceAndUp]: {
      paddingTop: 0,
    },
  },
  workExperienceContent: {
    marginTop: 16,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    backgroundColor: Colors.white200,

    [Breakpoints.mediumDeviceAndUp]: {
      marginTop: 24,
      padding: 16,
    },
  },
} satisfies Record<string, CSSObject>;
